import { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useStripeLoader, useStripeOnMount } from '@/provider/stripeLoader';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchInvoices,
  fetchPaymentMethods,
  fetchPrices,
  fetchSubscription,
  fetchUpgradePreview,
  fetchUsage,
  upgradeSubscription,
} from '@/features/billing/billingThunks';
import {
  selectPaymentMethods,
  selectPrices,
  selectSubscription,
  selectUpgradePreview,
  selectUpgradeLoading,
  selectUpgradeError,
} from '@/features/billing/billingSlice';
import type { StripePriceDto } from '@/features/billing/types';
import { formatPrice, resolveBillingCurrency } from '@/components/billing/usageExpiredUtils';
import { useTranslation } from 'react-i18next';

function getPriceByInterval(
  prices: StripePriceDto[] | null,
  planName: string,
  interval: 'month' | 'year',
): StripePriceDto | null {
  if (!prices?.length || !planName) return null;
  return (
    prices.find(
      (p) => p.interval === interval && p.nickname?.toLowerCase() === planName.toLowerCase(),
    ) ?? null
  );
}

export default function SubMonthlyToYearly() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const stripe = useStripe();
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const promoCode = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.promoCode);
  const subscription = useAppSelector(selectSubscription);
  const prices = useAppSelector(selectPrices);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const upgradePreview = useAppSelector(selectUpgradePreview);
  const upgradeLoading = useAppSelector(selectUpgradeLoading);
  const upgradeError = useAppSelector(selectUpgradeError);
  const pricesLoading = useAppSelector((s: RootState) => s.billing.prices.loading);
  const upgradePreviewLoading = useAppSelector((s: RootState) => s.billing.upgradePreview.loading);

  const [dialogOpen, setDialogOpen] = useState(false);
  useStripeOnMount(dialogOpen);
  const loadStripe = useStripeLoader();

  const isMonthly = subscription?.interval === 'month';
  const planName = subscription?.planName ?? '';
  const yearlyPrice = getPriceByInterval(prices, planName, 'year');
  const currency = yearlyPrice?.currency || resolveBillingCurrency(subscription?.currency);

  // Même règle que le changement de forfait : le tarif reconduit vient du preview Stripe, pas du catalogue.
  const yearlyReferencePrice = upgradePreview?.nextRecurringAmount ?? 0;
  const yearlyPriceDiscountAmount =
    yearlyPrice && yearlyReferencePrice > 0
      ? Math.max(0, yearlyPrice.amount - yearlyReferencePrice)
      : 0;

  useEffect(() => {
    if (workspaceId && isMonthly) {
      dispatch(fetchPrices({ workspaceId, ...(promoCode && { promoCode }) }));
    }
  }, [workspaceId, promoCode, isMonthly, dispatch]);

  useEffect(() => {
    if (dialogOpen && workspaceId) {
      if (yearlyPrice?.id) {
        dispatch(fetchUpgradePreview({ workspaceId, newPriceId: yearlyPrice.id }));
      }
      dispatch(fetchPaymentMethods(workspaceId));
    }
  }, [dialogOpen, workspaceId, yearlyPrice?.id, promoCode, dispatch]);

  const handleUpgrade = async () => {
    if (!workspaceId || !yearlyPrice?.id) return;
    try {
      const subscriptionResult = await dispatch(
        upgradeSubscription({
          workspaceId,
          input: { priceId: yearlyPrice.id },
        }),
      ).unwrap();

      if (subscriptionResult.paymentIntentClientSecret) {
        const pmToUse = paymentMethods?.find((pm) => pm.isDefault)?.id ?? paymentMethods?.[0]?.id;
        if (!pmToUse) {
          toast.error(t('upgradeToYearly.toast.noPaymentMethod'));
          return;
        }
        const stripeInstance = stripe ?? (await loadStripe());
        if (!stripeInstance) {
          toast.error(t('upgradeToYearly.toast.paymentFailed'));
          return;
        }
        const { error: paymentError } = await stripeInstance.confirmCardPayment(
          subscriptionResult.paymentIntentClientSecret,
          { payment_method: pmToUse },
        );
        if (paymentError) {
          const alreadySucceeded =
            paymentError.code === 'payment_intent_unexpected_state' &&
            paymentError.payment_intent?.status === 'succeeded';
          if (!alreadySucceeded) {
            toast.error(paymentError.message ?? t('upgradeToYearly.toast.paymentFailed'));
            return;
          }
        }
      }

      await Promise.all([
        dispatch(fetchSubscription(workspaceId)),
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchInvoices(workspaceId)),
      ]);
      toast.success(t('upgradeToYearly.toast.success'));
      setDialogOpen(false);
    } catch {
      // upgradeError is already set by the thunk rejection
    }
  };

  if (!isMonthly || !planName) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border border-card p-4 rounded-lg">
      <p className="text-text-secondary">{t('upgradeToYearly.description')}</p>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button className="w-full sm:w-auto">{t('upgradeToYearly.button')}</Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              {t('upgradeToYearly.dialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            {pricesLoading || upgradePreviewLoading ? (
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-2 text-sm text-secondary">
                <Loader />
                {t('upgradeToYearly.loadingPreview')}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    {t('upgradeToYearly.yearlySubscription')}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    {yearlyPrice ? formatPrice(yearlyPrice.amount, currency) : '–'}
                  </span>
                </div>
                {promoCode && yearlyPriceDiscountAmount > 0 ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        {t('subscription.promoCodeApplied')}{' '}
                        <span className="font-mono">({promoCode})</span>
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        -{formatPrice(yearlyPriceDiscountAmount, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        {t('upgradeToYearly.newYearlyPrice')}
                      </span>
                      <span className="text-base font-semibold text-gray-900">
                        {formatPrice(yearlyReferencePrice, currency)}
                      </span>
                    </div>
                  </>
                ) : null}
                {upgradePreview ? (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      {t('upgradeToYearly.prorata')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {(() => {
                        const proration = upgradePreview.immediateAmount - yearlyReferencePrice;
                        return `${proration > 0 ? '+' : ''}${formatPrice(proration, currency)}`;
                      })()}
                    </span>
                  </div>
                ) : null}
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-semibold text-gray-800">
                      {t('upgradeToYearly.totalToPay')}
                    </span>
                    <span className="text-xl font-bold text-green-600">
                      {upgradePreview ? formatPrice(upgradePreview.immediateAmount, currency) : '–'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {upgradeError && <p className="text-sm text-destructive">{upgradeError}</p>}
          </div>
          <div className="flex justify-center pt-2">
            <Button
              onClick={handleUpgrade}
              disabled={upgradeLoading || pricesLoading || upgradePreviewLoading || !upgradePreview}
              className="w-full"
            >
              {upgradeLoading && <Loader />}
              {upgradeLoading ? t('upgradeToYearly.payingButton') : t('upgradeToYearly.payButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
